import {
  useFetchComicNameAutocomplete,
  useFetchImageUploaderAIRecommendation,
  useFetchImageUploaderImageData,
  useFetchImageUploaderImageDetail,
  useFetchImageUploaderLastIds,
  useFetchMetaTagAutocomplete,
  usePatchImageUploaderImageData,
  usePostImageUploaderImageList,
  usePostImageUploaderPresignedUrl
} from '@api';
import {
  FemaleBodyChildOptionValueEnum,
  HorizontalAngleChildOptionValueEnum,
  ImageUploaderModalType,
  FrameChildOptionValueEnum,
  PathnameEnum,
  SexualBodyPartChildOptionValueEnum,
  SexualPositionChildOptionValueEnum,
  VerticalAngleChildOptionValueEnum,
  optionsConstant
} from '@constant';
import { SelectChangeEvent } from '@mui/material';
import {
  IChildOptionForPostImageListItem,
  IChildOptions,
  IComicName,
  IPatchImageUploadBody,
  IPostImageListItem,
  IUpdates,
  IUploadImageListItem
} from '@types';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

//이미지 업로더 - 이미지 업로드 및 이미지 정보 수정 페이지 로직에 관여
export const useImageUploader = () => {
  const { imageIdParam } = useParams<{ imageIdParam: string }>();
  const imageId = imageIdParam ? Number(imageIdParam) : undefined;
  const [searchParams] = useSearchParams();
  const sequenceId = searchParams.get('sequenceId') ? Number(searchParams.get('sequenceId')) : undefined;

  const isCreate = Boolean(!imageIdParam);

  //저장 버튼 disabled 처리 관련, 둘 중 하나라도 true라면 disabled={true}
  const [needImageAttach, setNeedImageAttach] = useState<boolean>(true);
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  //작품명 미입력시 파일 첨부 방지
  const [isUploadDisabled, setIsUploadDisabled] = useState<boolean>(false);
  //체위 Select 관련
  const [originalSelectedSexualPosition, setOriginalSelectedSexualPosition] = useState(
    SexualPositionChildOptionValueEnum.정상위
  );
  const [selectedSexualPosition, setSelectedSexualPosition] = useState(SexualPositionChildOptionValueEnum.정상위);
  //작품명 관련
  const [originalComic, setOriginalComic] = useState<IComicName>({ id: undefined, name: '' });
  const [autocompleteKeyword, setAutocompleteKeyword] = useState<string>('');
  const [inputKeyword, setInputKeyword] = useState<string>('');
  //이미지 리스트
  const [originalImageList, setOriginalImageList] = useState<IUploadImageListItem[]>([]);
  const [imageList, setImageList] = useState<IUploadImageListItem[]>([]);
  const [focusedImageIndex, setFocusedImageIndex] = useState<number | null>(null);
  const [prevImageListLength, setPrevImageListLength] = useState<number>(0); // 이미지 업로드 페이지 첫 이미지 업로드시 0번 자동 focus을 위한 state
  //태그 관련
  const [tagAutocompleteKeyword, setTagAutocompleteKeyword] = useState<string>('');
  const [tagInputKeyword, setTagInputKeyword] = useState<string>('');
  const [isTagging, setIsTagging] = useState(false); // 스페이스 입력 감지 상태
  //모달 관련
  const [modalType, setModalType] = useState<ImageUploaderModalType>(ImageUploaderModalType.IMAGE_UPLOAD_CANCEL);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalContent, setModalContent] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  //저장버튼 클릭시 error state 관련
  const [isSaveAttempted, setIsSaveAttempted] = useState(false);

  const navigate = useNavigate();

  const { data: lastIdsData } = useFetchImageUploaderLastIds();
  const lastImageId = lastIdsData ? lastIdsData.lastImageId : 1;
  const lastSequenceId = lastIdsData ? lastIdsData.lastSequenceId : 1;

  const { data: comicNameAutocompleteData } = useFetchComicNameAutocomplete(
    autocompleteKeyword && autocompleteKeyword.length > 0 ? autocompleteKeyword : null
  );
  const autocompleteList =
    autocompleteKeyword &&
    autocompleteKeyword.length > 0 &&
    comicNameAutocompleteData &&
    comicNameAutocompleteData.length > 0
      ? comicNameAutocompleteData.map((item) => item.name)
      : [];

  const { data: metaTagAutocompleteData } = useFetchMetaTagAutocomplete(
    tagAutocompleteKeyword && tagAutocompleteKeyword.length > 0 ? tagAutocompleteKeyword : null
  );
  const tagAutocompleteList =
    tagAutocompleteKeyword &&
    tagAutocompleteKeyword.length > 0 &&
    metaTagAutocompleteData &&
    metaTagAutocompleteData.length > 0
      ? metaTagAutocompleteData.map((item) => item.name)
      : [];

  const { mutateAsync: postPresignedUrlMutateAsync } = usePostImageUploaderPresignedUrl();

  const { mutateAsync: fetchAIRecommendationMutateAsync, isPending: isFetchAIRecommendationLoading } =
    useFetchImageUploaderAIRecommendation();

  const { mutate: postImageListMutate } = usePostImageUploaderImageList(
    () => {
      setModalType(ImageUploaderModalType.IMAGE_UPLOAD_SUCCESS);
      setModalTitle('업로드에 성공했습니다.');
      setModalContent('리스트로 돌아갑니다.');
      setIsModalVisible(true);
    },
    () => {
      setModalType(ImageUploaderModalType.IMAGE_UPLOAD_REQUEST_FAIL);
      setModalTitle('업로드에 실패하였습니다.');
      setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
      setIsModalVisible(true);
      setIsRequestPending(false); //저장버튼 활성화
    }
  );

  const { data: fetchedImageData } = useFetchImageUploaderImageData(imageId, sequenceId);

  const { mutateAsync: fetchImageDetailMutateAsync } = useFetchImageUploaderImageDetail();

  const { mutate: patchImageDataMutate } = usePatchImageUploaderImageData(
    () => {
      setModalType(ImageUploaderModalType.IMAGE_UPLOAD_SUCCESS);
      setModalTitle('업로드에 성공했습니다.');
      setModalContent('리스트로 돌아갑니다.');
      setIsModalVisible(true);
    },
    () => {
      setModalType(ImageUploaderModalType.IMAGE_UPLOAD_REQUEST_FAIL);
      setModalTitle('업로드에 실패하였습니다.');
      setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
      setIsModalVisible(true);
      setIsRequestPending(false); //저장버튼 활성화
    }
  );

  // 현재 focus된 이미지 반환
  const focusedImage = focusedImageIndex !== null ? imageList[focusedImageIndex] : null;

  const handleBreadCrumbClick = () => {
    navigate(PathnameEnum.IMAGE_UPLOADER_ADULTS_ONLY_IMAGE_LIST_PAGE);
  };

  const handlePageCancelClick = () => {
    setModalType(ImageUploaderModalType.IMAGE_UPLOAD_CANCEL);
    setModalTitle('이미지 업로드를 중단하시겠습니까?');
    setModalContent('이 작업은 되돌릴 수 없습니다.');
    setIsModalVisible(true);
  };

  const handleSaveButtonClick = () => {
    setIsRequestPending(true);

    const comicNameError = inputKeyword.trim().length === 0;

    // 필수 childOptions key 목록
    const requiredKeys = ['femaleBody', 'sexualBodyPart', 'characterNumber', 'shot', 'angleFirst', 'angleSecond'];

    // imageList의 모든 요소가 childOptions에 위 key를 모두 갖고, 값이 undefined 또는 null이 아닌지 검사
    const imageChildOptionValueError =
      imageList.length === 0
        ? false
        : !imageList
            .filter((item) => item.childOptions !== null) // childOptions가 null이 아닌 이미지만 필터링
            .every((item) =>
              requiredKeys.every(
                (key) =>
                  Object.prototype.hasOwnProperty.call(item.childOptions, key) &&
                  item.childOptions &&
                  item.childOptions[key]
              )
            );

    if (comicNameError || imageChildOptionValueError) {
      setModalType(ImageUploaderModalType.OMIT_REQUIRED_INPUT);
      setModalTitle('업로드에 실패했습니다.');
      setModalContent('필수 입력란을 채워주세요.');
      setIsModalVisible(true);
      setIsRequestPending(false); //저장버튼 활성화

      return;
    }

    sendPostOrPatch(imageList);
  };

  // 특정 이미지에 focus를 주는 함수
  const handleImageFocus = (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    //우측 이미지 정보란에 focusedImage의 정보를 보여주기 위함
    setFocusedImageIndex(targetImageIndex);

    //이미지 리스트의 이미지 타일에 focus 효과 주기 위해 isFocus: true setState
    const newImageList = imageList.map((item, idx) => ({
      ...item,
      isFocus: idx === targetImageIndex
    }));

    setImageList(newImageList);

    // 새로 업로드된 이미지의 경우 POST /images/presigned-url 요청을 invoke
    if (!targetImage?.imageId && !targetImage?.key && !targetImage?.presignedUrl) {
      sendPostPresignedUrl(newImageList[targetImageIndex], targetImageIndex);
    }

    // 기존에 업로드된 이미지의 경우 focusedImage의 정보를 불러오기
    if (targetImage?.imageId && (!targetImage?.childOptions || !targetImage?.aiRecommendedChildOptions)) {
      getImageDetail(newImageList[targetImageIndex], targetImageIndex);
    }
  };

  // GET /admin/images/:imageId/detail 요청 전송 및 GET /images/tag-suggestions 요청을 invoke
  const getImageDetail = async (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    // 기존에 업로드된 이미지 중 아직 child option값을 불러오지 않는 이미지에 한해 요청 전송
    if (targetImage?.imageId && !targetImage.childOptions) {
      await fetchImageDetailMutateAsync(targetImage?.imageId).then((res) => {
        const fetchedImageDetailData = res.data.data;

        setImageList((prev) => {
          // 기존 리스트 복사
          const newList = prev.map((item) => {
            if (item.imageId !== targetImage.imageId) return item;

            const { imageOptionValue, metaTag } = fetchedImageDetailData;

            setSelectedSexualPosition(imageOptionValue.체위 as SexualPositionChildOptionValueEnum);
            setOriginalSelectedSexualPosition(imageOptionValue.체위 as SexualPositionChildOptionValueEnum); //PATCH request body에 체위값 실을지 여부 결정하기 위한 비교값

            // 옵션 및 태그값 업데이트 여부 확인을 위해 original 프로퍼티를 함께 생성
            return {
              ...item,
              originalChildOptions: {
                sexualPosition: imageOptionValue.체위,
                femaleBody:
                  imageOptionValue.여체 && imageOptionValue.여체.length > 0
                    ? {
                        슬랜더: {
                          name: '슬랜더',
                          value: FemaleBodyChildOptionValueEnum['슬랜더'],
                          isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슬랜더']
                        },
                        글래머: {
                          name: '글래머',
                          value: FemaleBodyChildOptionValueEnum['글래머'],
                          isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['글래머']
                        },
                        슈퍼글래머: {
                          name: '슈퍼글래머',
                          value: FemaleBodyChildOptionValueEnum['슈퍼글래머'],
                          isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슈퍼글래머']
                        },
                        통통: {
                          name: '통통',
                          value: FemaleBodyChildOptionValueEnum['통통'],
                          isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['통통']
                        }
                      }
                    : null,
                sexualBodyPart:
                  imageOptionValue.신체부위 && imageOptionValue.신체부위.length > 0
                    ? {
                        가슴: {
                          name: '가슴',
                          value: SexualBodyPartChildOptionValueEnum['가슴'],
                          isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['가슴'])
                        },
                        엉덩이: {
                          name: '엉덩이',
                          value: SexualBodyPartChildOptionValueEnum['엉덩이'],
                          isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['엉덩이'])
                        },
                        '여자 성기': {
                          name: '여자 성기',
                          value: SexualBodyPartChildOptionValueEnum['여자 성기'],
                          isSelected: imageOptionValue.신체부위.includes(
                            SexualBodyPartChildOptionValueEnum['여자 성기']
                          )
                        },
                        '남자 성기': {
                          name: '남자 성기',
                          value: SexualBodyPartChildOptionValueEnum['남자 성기'],
                          isSelected: imageOptionValue.신체부위.includes(
                            SexualBodyPartChildOptionValueEnum['남자 성기']
                          )
                        },
                        입: {
                          name: '입',
                          value: SexualBodyPartChildOptionValueEnum['입'],
                          isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['입'])
                        },
                        얼굴: {
                          name: '얼굴',
                          value: SexualBodyPartChildOptionValueEnum['얼굴'],
                          isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['얼굴'])
                        }
                      }
                    : null,
                characterNumber:
                  imageOptionValue['남성 인원수'] != null && imageOptionValue['여성 인원수'] != null
                    ? {
                        maleNumber: imageOptionValue['남성 인원수'],
                        femaleNumber: imageOptionValue['여성 인원수']
                      }
                    : null,
                shot:
                  imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                    ? {
                        풀샷: {
                          name: '풀샷',
                          value: FrameChildOptionValueEnum['풀샷'],
                          isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['풀샷']
                        },
                        미디엄샷: {
                          name: '미디엄샷',
                          value: FrameChildOptionValueEnum['미디엄샷'],
                          isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['미디엄샷']
                        },
                        클로즈업: {
                          name: '클로즈업',
                          value: FrameChildOptionValueEnum['클로즈업'],
                          isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['클로즈업']
                        }
                      }
                    : null,
                angleFirst:
                  imageOptionValue['가로앵글'] && imageOptionValue['가로앵글'].length > 0
                    ? {
                        정면: {
                          name: '정면',
                          value: HorizontalAngleChildOptionValueEnum['정면'],
                          isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['정면'])
                        },
                        정측면: {
                          name: '정측면',
                          value: HorizontalAngleChildOptionValueEnum['정측면'],
                          isSelected: imageOptionValue['가로앵글'].includes(
                            HorizontalAngleChildOptionValueEnum['정측면']
                          )
                        },
                        측면: {
                          name: '측면',
                          value: HorizontalAngleChildOptionValueEnum['측면'],
                          isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['측면'])
                        },
                        후측면: {
                          name: '후측면',
                          value: HorizontalAngleChildOptionValueEnum['후측면'],
                          isSelected: imageOptionValue['가로앵글'].includes(
                            HorizontalAngleChildOptionValueEnum['후측면']
                          )
                        },
                        후면: {
                          name: '후면',
                          value: HorizontalAngleChildOptionValueEnum['후면'],
                          isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['후면'])
                        }
                      }
                    : null,
                angleSecond:
                  imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                    ? {
                        로우앵글: {
                          name: '로우앵글',
                          value: VerticalAngleChildOptionValueEnum['로우앵글'],
                          isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                        },
                        하이앵글: {
                          name: '하이앵글',
                          value: VerticalAngleChildOptionValueEnum['하이앵글'],
                          isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                        },
                        항공샷: {
                          name: '항공샷',
                          value: VerticalAngleChildOptionValueEnum['항공샷'],
                          isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                        },
                        평시각: {
                          name: '평시각',
                          value: VerticalAngleChildOptionValueEnum['평시각'],
                          isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                        }
                      }
                    : null
              },
              childOptions: {
                sexualPosition: imageOptionValue.체위,
                femaleBody:
                  imageOptionValue.여체 && imageOptionValue.여체.length > 0
                    ? {
                        슬랜더: {
                          name: '슬랜더',
                          value: FemaleBodyChildOptionValueEnum['슬랜더'],
                          isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슬랜더']
                        },
                        글래머: {
                          name: '글래머',
                          value: FemaleBodyChildOptionValueEnum['글래머'],
                          isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['글래머']
                        },
                        슈퍼글래머: {
                          name: '슈퍼글래머',
                          value: FemaleBodyChildOptionValueEnum['슈퍼글래머'],
                          isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슈퍼글래머']
                        },
                        통통: {
                          name: '통통',
                          value: FemaleBodyChildOptionValueEnum['통통'],
                          isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['통통']
                        }
                      }
                    : null,
                sexualBodyPart:
                  imageOptionValue.신체부위 && imageOptionValue.신체부위.length > 0
                    ? {
                        가슴: {
                          name: '가슴',
                          value: SexualBodyPartChildOptionValueEnum['가슴'],
                          isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['가슴'])
                        },
                        엉덩이: {
                          name: '엉덩이',
                          value: SexualBodyPartChildOptionValueEnum['엉덩이'],
                          isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['엉덩이'])
                        },
                        '여자 성기': {
                          name: '여자 성기',
                          value: SexualBodyPartChildOptionValueEnum['여자 성기'],
                          isSelected: imageOptionValue.신체부위.includes(
                            SexualBodyPartChildOptionValueEnum['여자 성기']
                          )
                        },
                        '남자 성기': {
                          name: '남자 성기',
                          value: SexualBodyPartChildOptionValueEnum['남자 성기'],
                          isSelected: imageOptionValue.신체부위.includes(
                            SexualBodyPartChildOptionValueEnum['남자 성기']
                          )
                        },
                        입: {
                          name: '입',
                          value: SexualBodyPartChildOptionValueEnum['입'],
                          isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['입'])
                        },
                        얼굴: {
                          name: '얼굴',
                          value: SexualBodyPartChildOptionValueEnum['얼굴'],
                          isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['얼굴'])
                        }
                      }
                    : null,
                characterNumber:
                  imageOptionValue['남성 인원수'] != null && imageOptionValue['여성 인원수'] != null
                    ? {
                        maleNumber: imageOptionValue['남성 인원수'],
                        femaleNumber: imageOptionValue['여성 인원수']
                      }
                    : null,
                shot:
                  imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                    ? {
                        풀샷: {
                          name: '풀샷',
                          value: FrameChildOptionValueEnum['풀샷'],
                          isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['풀샷']
                        },
                        미디엄샷: {
                          name: '미디엄샷',
                          value: FrameChildOptionValueEnum['미디엄샷'],
                          isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['미디엄샷']
                        },
                        클로즈업: {
                          name: '클로즈업',
                          value: FrameChildOptionValueEnum['클로즈업'],
                          isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['클로즈업']
                        }
                      }
                    : null,
                angleFirst:
                  imageOptionValue['가로앵글'] && imageOptionValue['가로앵글'].length > 0
                    ? {
                        정면: {
                          name: '정면',
                          value: HorizontalAngleChildOptionValueEnum['정면'],
                          isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['정면'])
                        },
                        정측면: {
                          name: '정측면',
                          value: HorizontalAngleChildOptionValueEnum['정측면'],
                          isSelected: imageOptionValue['가로앵글'].includes(
                            HorizontalAngleChildOptionValueEnum['정측면']
                          )
                        },
                        측면: {
                          name: '측면',
                          value: HorizontalAngleChildOptionValueEnum['측면'],
                          isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['측면'])
                        },
                        후측면: {
                          name: '후측면',
                          value: HorizontalAngleChildOptionValueEnum['후측면'],
                          isSelected: imageOptionValue['가로앵글'].includes(
                            HorizontalAngleChildOptionValueEnum['후측면']
                          )
                        },
                        후면: {
                          name: '후면',
                          value: HorizontalAngleChildOptionValueEnum['후면'],
                          isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['후면'])
                        }
                      }
                    : null,
                angleSecond:
                  imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                    ? {
                        로우앵글: {
                          name: '로우앵글',
                          value: VerticalAngleChildOptionValueEnum['로우앵글'],
                          isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                        },
                        하이앵글: {
                          name: '하이앵글',
                          value: VerticalAngleChildOptionValueEnum['하이앵글'],
                          isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                        },
                        항공샷: {
                          name: '항공샷',
                          value: VerticalAngleChildOptionValueEnum['항공샷'],
                          isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                        },
                        평시각: {
                          name: '평시각',
                          value: VerticalAngleChildOptionValueEnum['평시각'],
                          isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                        }
                      }
                    : null
              },
              originalTags: metaTag.map((tag) => tag.name),
              tags: metaTag.map((tag) => tag.name)
            };
          });
          return newList;
        });
      });
    }

    if (!targetImage.aiRecommendedChildOptions) {
      getAIRecommendation(targetImage, targetImageIndex);
    }
  };

  const sendPostPresignedUrl = async (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    const requestBody = [{ name: targetImage.name, type: targetImage.type }];

    await postPresignedUrlMutateAsync(requestBody)
      .then(async (res) => {
        // POST /images/presigned-url 응답의 key와 presignedUrl 프로퍼티를 imageList 요소에 할당
        const presignedData = res.data.data;

        // POST 및 PATCH /admin/images 요청을 위해 key와 presignedUrl 프로퍼티를 imageList 요소에 할당
        setImageList((prev) => {
          const imageListWithKeyAndPresignedUrl = prev.map((item, idx) => {
            if (idx === targetImageIndex) {
              return {
                ...item,
                key: presignedData[0].key,
                presignedUrl: presignedData[0].presignedUrl
              };
            }
            return item;
          });

          // 업데이트된 이미지로 S3 업로드 실행
          uploadImageToS3(imageListWithKeyAndPresignedUrl[targetImageIndex], targetImageIndex);

          return imageListWithKeyAndPresignedUrl;
        });
      })
      .catch((error) => {
        console.log('postPresignedUrlMutateAsync error: ', error);

        // 해당 이미지의 key와 presignedUrl값을 imageList 요소로부터 삭제
        setImageList((prev) => {
          return prev.map((item, idx) => {
            if (idx === targetImageIndex) {
              const { key, presignedUrl, ...rest } = item;
              return rest;
            }
            return item;
          });
        });

        setModalType(ImageUploaderModalType.IMAGE_UPLOAD_REQUEST_FAIL);
        setModalTitle('업로드에 실패하였습니다.');
        setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
        setIsModalVisible(true);
        setIsRequestPending(false); //저장버튼 활성화
      });
  };

  const uploadImageToS3 = async (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    await fetch(targetImage.presignedUrl ?? '', {
      method: 'PUT',
      body: targetImage.file,
      headers: { 'Content-Type': targetImage.type }
    })
      .then(() => {
        if (!targetImage.aiRecommendedChildOptions) {
          getAIRecommendation(targetImage, targetImageIndex);
        }
      })
      .catch((error) => {
        console.log('uploadImageToS3 error: ', error);

        // 해당 이미지의 key와 presignedUrl값을 imageList 요소로부터 삭제
        setImageList((prev) => {
          return prev.map((item, idx) => {
            if (idx === targetImageIndex) {
              const { key, presignedUrl, ...rest } = item;
              return rest;
            }
            return item;
          });
        });

        setModalType(ImageUploaderModalType.IMAGE_UPLOAD_REQUEST_FAIL);
        setModalTitle('업로드에 실패하였습니다.');
        setModalContent('요청이 실패하였습니다. 다시 시도해주세요.');
        setIsModalVisible(true);
        setIsRequestPending(false);
      });
  };

  const getAIRecommendation = async (targetImage: IUploadImageListItem, targetImageIndex: number) => {
    let s3Path, key;

    // 새로 업로드된 이미지는 key값을 갖는다
    if (targetImage.key) {
      // 가장 먼저 등장하는 슬래시의 인덱스를 찾는다
      const slashIndex = targetImage.key.indexOf('/');

      if (slashIndex !== -1) {
        s3Path = targetImage.key.slice(0, slashIndex); // 슬래시 이전 부분 'temp'
        key = targetImage.key.slice(slashIndex + 1); // 슬래시 이후 부분 e.g. IMG-uuid-fileName.png
      } else {
        // 슬래시가 없는 경우 전체를 파일명으로 간주
        s3Path = '';
        key = targetImage.key;
      }
    }
    // 기존에 업로드된 이미지는 uniqueFileName값을 갖는다
    else if (targetImage.uniqueFileName) {
      s3Path = 'images';
      key = targetImage.uniqueFileName;
    }

    if (s3Path && key) {
      await fetchAIRecommendationMutateAsync({ s3Path: s3Path, key: key })
        .then((res) => {
          const fetchedAIRecommendationData = res.data.data;

          setImageList((prev) => {
            // 기존 리스트 복사
            const newList = prev.map((item, index) => {
              if (index !== targetImageIndex) return item;

              const { imageOptionValue, tags } = fetchedAIRecommendationData;

              return {
                ...item,
                aiRecommendedChildOptions: {
                  sexualPosition: selectedSexualPosition,
                  //femaleBody, shot, angleFirst, angleSecond의 경우 빈 문자열 응답시 null값 부여
                  femaleBody:
                    imageOptionValue.여체 && imageOptionValue.여체.length > 0
                      ? {
                          슬랜더: {
                            name: '슬랜더',
                            value: FemaleBodyChildOptionValueEnum['슬랜더'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슬랜더']
                          },
                          글래머: {
                            name: '글래머',
                            value: FemaleBodyChildOptionValueEnum['글래머'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['글래머']
                          },
                          슈퍼글래머: {
                            name: '슈퍼글래머',
                            value: FemaleBodyChildOptionValueEnum['슈퍼글래머'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['슈퍼글래머']
                          },
                          통통: {
                            name: '통통',
                            value: FemaleBodyChildOptionValueEnum['통통'],
                            isSelected: imageOptionValue.여체 === FemaleBodyChildOptionValueEnum['통통']
                          }
                        }
                      : null,
                  sexualBodyPart:
                    imageOptionValue.신체부위 && imageOptionValue.신체부위.length > 0
                      ? {
                          가슴: {
                            name: '가슴',
                            value: SexualBodyPartChildOptionValueEnum['가슴'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['가슴'])
                          },
                          엉덩이: {
                            name: '엉덩이',
                            value: SexualBodyPartChildOptionValueEnum['엉덩이'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['엉덩이'])
                          },
                          '여자 성기': {
                            name: '여자 성기',
                            value: SexualBodyPartChildOptionValueEnum['여자 성기'],
                            isSelected: imageOptionValue.신체부위.includes(
                              SexualBodyPartChildOptionValueEnum['여자 성기']
                            )
                          },
                          '남자 성기': {
                            name: '남자 성기',
                            value: SexualBodyPartChildOptionValueEnum['남자 성기'],
                            isSelected: imageOptionValue.신체부위.includes(
                              SexualBodyPartChildOptionValueEnum['남자 성기']
                            )
                          },
                          입: {
                            name: '입',
                            value: SexualBodyPartChildOptionValueEnum['입'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['입'])
                          },
                          얼굴: {
                            name: '얼굴',
                            value: SexualBodyPartChildOptionValueEnum['얼굴'],
                            isSelected: imageOptionValue.신체부위.includes(SexualBodyPartChildOptionValueEnum['얼굴'])
                          }
                        }
                      : null,
                  characterNumber:
                    imageOptionValue['남성 인원수'] != null && imageOptionValue['여성 인원수'] != null
                      ? {
                          maleNumber: imageOptionValue['남성 인원수'],
                          femaleNumber: imageOptionValue['여성 인원수']
                        }
                      : null,
                  shot:
                    imageOptionValue['샷 구분'] && imageOptionValue['샷 구분'].length > 0
                      ? {
                          풀샷: {
                            name: '풀샷',
                            value: FrameChildOptionValueEnum['풀샷'],
                            isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['풀샷']
                          },
                          미디엄샷: {
                            name: '미디엄샷',
                            value: FrameChildOptionValueEnum['미디엄샷'],
                            isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['미디엄샷']
                          },
                          클로즈업: {
                            name: '클로즈업',
                            value: FrameChildOptionValueEnum['클로즈업'],
                            isSelected: imageOptionValue['샷 구분'] === FrameChildOptionValueEnum['클로즈업']
                          }
                        }
                      : null,
                  angleFirst:
                    imageOptionValue['가로앵글'] && imageOptionValue['가로앵글'].length > 0
                      ? {
                          정면: {
                            name: '정면',
                            value: HorizontalAngleChildOptionValueEnum['정면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정면']
                            )
                          },
                          정측면: {
                            name: '정측면',
                            value: HorizontalAngleChildOptionValueEnum['정측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['정측면']
                            )
                          },
                          측면: {
                            name: '측면',
                            value: HorizontalAngleChildOptionValueEnum['측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['측면']
                            )
                          },
                          후측면: {
                            name: '후측면',
                            value: HorizontalAngleChildOptionValueEnum['후측면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후측면']
                            )
                          },
                          후면: {
                            name: '후면',
                            value: HorizontalAngleChildOptionValueEnum['후면'],
                            isSelected: imageOptionValue['가로앵글'].includes(
                              HorizontalAngleChildOptionValueEnum['후면']
                            )
                          }
                        }
                      : null,
                  angleSecond:
                    imageOptionValue['세로앵글'] && imageOptionValue['세로앵글'].length > 0
                      ? {
                          로우앵글: {
                            name: '로우앵글',
                            value: VerticalAngleChildOptionValueEnum['로우앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['로우앵글']
                          },
                          하이앵글: {
                            name: '하이앵글',
                            value: VerticalAngleChildOptionValueEnum['하이앵글'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['하이앵글']
                          },
                          항공샷: {
                            name: '항공샷',
                            value: VerticalAngleChildOptionValueEnum['항공샷'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['항공샷']
                          },
                          평시각: {
                            name: '평시각',
                            value: VerticalAngleChildOptionValueEnum['평시각'],
                            isSelected: imageOptionValue['세로앵글'] === VerticalAngleChildOptionValueEnum['평시각']
                          }
                        }
                      : null
                },
                aiRecommendedTags: tags ?? []
              };
            });
            return newList;
          });
        })
        .catch((error) => {
          console.log('fetchAIRecommendationMutateAsync error: ', error);
        });
    }
  };

  const sendPostOrPatch = (imageList: IUploadImageListItem[]) => {
    if (isCreate) {
      sendPostImageList(imageList);
    } else {
      sendPatchImageData(imageList);
    }
  };

  const sendPostImageList = (imageList: IUploadImageListItem[]) => {
    const requestBody: IPostImageListItem[] = imageList.map((item) => {
      // childOptions 변환

      const childOptions: IChildOptionForPostImageListItem = {
        // 1, 총 인원수
        [optionsConstant.childOptions['wholeGenderNumber'].id]: Number(
          ((item.childOptions?.characterNumber as { maleNumber?: number })?.maleNumber ?? 0) +
            ((item.childOptions?.characterNumber as { femaleNumber?: number })?.femaleNumber ?? 0)
        ),
        // 2, 남성 인원수
        [optionsConstant.childOptions['maleNumber'].id]: Number(
          (item.childOptions?.characterNumber as { maleNumber?: number })?.maleNumber ?? 0
        ),
        // 3, 여성 인원수
        [optionsConstant.childOptions['femaleNumber'].id]: Number(
          (item.childOptions?.characterNumber as { femaleNumber?: number })?.femaleNumber ?? 0
        ),
        // 4, 샷 구분(프레임)
        [optionsConstant.childOptions['shot'].id]: Object.values(item.childOptions?.shot ?? {}).find(
          (option) => option.isSelected
        ).value,
        // 16, 여체
        [optionsConstant.childOptions.femaleBody.id]: Object.values(item.childOptions?.femaleBody ?? {}).find(
          (option) => option.isSelected
        ).value,
        // 18, 체위
        [optionsConstant.childOptions.sexualPosition.id]: selectedSexualPosition,
        // 22, 신체부위
        [optionsConstant.childOptions.sexualBodyPart.id]: Object.values(item.childOptions?.sexualBodyPart ?? {})
          .filter((option) => option.isSelected)
          .map((option) => String(option.value)),
        // 25, 가로앵글
        [optionsConstant.childOptions['angleFirst'].id]: Object.values(item.childOptions?.angleFirst ?? {})
          .filter((option) => option.isSelected)
          .map((option) => String(option.value)),
        // 26, 세로앵글
        [optionsConstant.childOptions['angleSecond'].id]: Object.values(item.childOptions?.angleSecond ?? {}).find(
          (option) => option.isSelected
        ).value
      };

      return {
        name: item.name,
        key: item.key ?? '',
        tags: item.tags ?? [],
        order: item.order ?? 0,
        comic: item.comic,
        childOptions
      };
    });

    postImageListMutate(requestBody);
  };

  const sendPatchImageData = (imageList: IUploadImageListItem[]) => {
    const requestBody: IPatchImageUploadBody = {};

    // 작품명을 변경한 경우
    if (originalComic.name.trim() !== inputKeyword.trim() && originalComic.id) {
      //requestBody에 프로퍼티 추가
      requestBody.newComicName = {
        id: originalComic.id,
        name: inputKeyword.trim()
      };
    }

    // order가 변경된 이미지들 찾기
    const moves = imageList
      .filter((item) => 'imageId' in item) // 기존 이미지만 필터링
      .filter((item) => {
        const originalItem = originalImageList.find((original) => original.imageId === item.imageId);
        return originalItem && originalItem.order !== item.order;
      })
      .map((item) => ({
        imageId: item.imageId!,
        name: item.name,
        newOrder: item.order!
      }));

    if (moves.length > 0) {
      requestBody.moves = moves;
    }

    // childOptions와 tags가 변경된 이미지들 찾기
    const updates = imageList
      .filter((item) => 'imageId' in item) // 기존 이미지만 필터링
      .map((item) => {
        const update: IUpdates = {
          imageId: item.imageId!
        };

        // childOptions 처리
        const changedChildOptions: IChildOptions = {};

        if (item.childOptions !== null) {
          // femaleBody 변경 확인
          const originalFemaleBody = Object.values(item.originalChildOptions?.femaleBody ?? {}).find(
            (option) => option.isSelected
          )?.value;
          const currentFemaleBody = Object.values(item.childOptions.femaleBody ?? {}).find(
            (option) => option.isSelected
          )?.value;
          if (originalFemaleBody !== currentFemaleBody) {
            changedChildOptions[optionsConstant.childOptions.femaleBody.id] = currentFemaleBody;
          }

          // sexualBodyPart 변경 확인
          const originalSexualBodyPart = Object.values(item.originalChildOptions?.sexualBodyPart ?? {})
            .filter((option) => option.isSelected)
            .map((option) => String(option.value))
            .sort();
          const currentSexualBodyPart = Object.values(item.childOptions.sexualBodyPart ?? {})
            .filter((option) => option.isSelected)
            .map((option) => String(option.value))
            .sort();
          if (JSON.stringify(originalSexualBodyPart) !== JSON.stringify(currentSexualBodyPart)) {
            changedChildOptions[optionsConstant.childOptions.sexualBodyPart.id] = currentSexualBodyPart;
          }

          // characterNumber 변경 확인
          const originalCharacterNumber = item.originalChildOptions?.characterNumber as {
            maleNumber?: number;
            femaleNumber?: number;
          };
          const currentCharacterNumber = item.childOptions.characterNumber as {
            maleNumber?: number;
            femaleNumber?: number;
          };
          if (
            originalCharacterNumber?.maleNumber !== currentCharacterNumber?.maleNumber ||
            originalCharacterNumber?.femaleNumber !== currentCharacterNumber?.femaleNumber
          ) {
            changedChildOptions[optionsConstant.childOptions['maleNumber'].id] = currentCharacterNumber?.maleNumber;
            changedChildOptions[optionsConstant.childOptions['femaleNumber'].id] = currentCharacterNumber?.femaleNumber;
            changedChildOptions[optionsConstant.childOptions['wholeGenderNumber'].id] =
              (currentCharacterNumber?.maleNumber ?? 0) + (currentCharacterNumber?.femaleNumber ?? 0);
          }

          // shot 변경 확인
          const originalShot = Object.values(item.originalChildOptions?.shot ?? {}).find(
            (option) => option.isSelected
          )?.value;
          const currentShot = Object.values(item.childOptions.shot ?? {}).find((option) => option.isSelected)?.value;
          if (originalShot !== currentShot) {
            changedChildOptions[optionsConstant.childOptions['shot'].id] = currentShot;
          }

          // 가로앵글 변경 확인
          const originalHorizontalAngle = Object.values(item.originalChildOptions?.angleFirst ?? {})
            .filter((option) => option.isSelected)
            .map((option) => String(option.value))
            .sort();
          const currentHorizontalAngle = Object.values(item.childOptions.angleFirst ?? {})
            .filter((option) => option.isSelected)
            .map((option) => String(option.value))
            .sort();
          if (JSON.stringify(originalHorizontalAngle) !== JSON.stringify(currentHorizontalAngle)) {
            changedChildOptions[optionsConstant.childOptions.angleFirst.id] = currentHorizontalAngle;
          }

          // 세로앵글 변경 확인
          const originalVerticalAngle = Object.values(item.originalChildOptions?.angleSecond ?? {}).find(
            (option) => option.isSelected
          )?.value;
          const currentVerticalAngle = Object.values(item.childOptions.angleSecond ?? {}).find(
            (option) => option.isSelected
          )?.value;
          if (originalVerticalAngle !== currentVerticalAngle) {
            changedChildOptions[optionsConstant.childOptions['angleSecond'].id] = currentVerticalAngle;
          }
        }

        // 체위의 경우 item.childOptions가 null이더라도 originalSelectedSexualPosition과 selectedSexualPosition값이 다르다면 updates 배열에 실려야 한다.
        // sexualPosition 변경 확인
        if (originalSelectedSexualPosition !== selectedSexualPosition) {
          changedChildOptions[optionsConstant.childOptions.sexualPosition.id] = selectedSexualPosition;
        }

        // 변경된 childOptions가 있는 경우에만 추가, 없으면 undefined
        if (Object.keys(changedChildOptions).length > 0) {
          const childOptions: IChildOptions = {
            // 1, 총 인원수
            [optionsConstant.childOptions['wholeGenderNumber'].id]: Number(
              ((item.childOptions?.characterNumber as { maleNumber?: number })?.maleNumber ?? 0) +
                ((item.childOptions?.characterNumber as { femaleNumber?: number })?.femaleNumber ?? 0)
            ),
            // 2, 남성 인원수
            [optionsConstant.childOptions['maleNumber'].id]: Number(
              (item.childOptions?.characterNumber as { maleNumber?: number })?.maleNumber ?? 0
            ),
            // 3, 여성 인원수
            [optionsConstant.childOptions['femaleNumber'].id]: Number(
              (item.childOptions?.characterNumber as { femaleNumber?: number })?.femaleNumber ?? 0
            ),
            // 4, 샷 구분(프레임)
            [optionsConstant.childOptions['shot'].id]: Object.values(item.childOptions?.shot ?? {}).find(
              (option) => option.isSelected
            ).value,
            // 16, 여체
            [optionsConstant.childOptions.femaleBody.id]: Object.values(item.childOptions?.femaleBody ?? {}).find(
              (option) => option.isSelected
            ).value,
            // 18, 체위
            [optionsConstant.childOptions.sexualPosition.id]: selectedSexualPosition,
            // 22, 신체부위
            [optionsConstant.childOptions.sexualBodyPart.id]: Object.values(item.childOptions?.sexualBodyPart ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value)),
            // 25, 가로앵글
            [optionsConstant.childOptions['angleFirst'].id]: Object.values(item.childOptions?.angleFirst ?? {})
              .filter((option) => option.isSelected)
              .map((option) => String(option.value)),
            // 26, 세로앵글
            [optionsConstant.childOptions['angleSecond'].id]: Object.values(item.childOptions?.angleSecond ?? {}).find(
              (option) => option.isSelected
            ).value
          };

          update.childOptions = childOptions;
        } else {
          update.childOptions = undefined;
        }

        // tags 처리
        if (item.tags !== null) {
          const originalTags = item.originalTags?.sort() ?? [];
          const currentTags = item.tags.sort();
          if (JSON.stringify(originalTags) !== JSON.stringify(currentTags)) {
            update.tags = currentTags;
          }
        }

        return update;
      })
      .filter((update) => update.childOptions !== undefined || update.tags !== undefined); // 변경사항이 있는 항목만 필터링

    if (updates.length > 0) {
      requestBody.updates = updates;
    }

    // 새로 업로드된 이미지들 찾기
    const addImages = imageList
      .filter((item) => !('imageId' in item))
      .map((item) => ({
        name: item.name,
        key: item.key!,
        order: item.order!,
        tags: item.tags ?? [],
        childOptions: {
          // 1, 총 인원수
          [optionsConstant.childOptions['wholeGenderNumber'].id]: Number(
            ((item.childOptions?.characterNumber as { maleNumber?: number })?.maleNumber ?? 0) +
              ((item.childOptions?.characterNumber as { femaleNumber?: number })?.femaleNumber ?? 0)
          ),
          // 2, 남성 인원수
          [optionsConstant.childOptions['maleNumber'].id]: Number(
            (item.childOptions?.characterNumber as { maleNumber?: number })?.maleNumber ?? 0
          ),
          // 3, 여성 인원수
          [optionsConstant.childOptions['femaleNumber'].id]: Number(
            (item.childOptions?.characterNumber as { femaleNumber?: number })?.femaleNumber ?? 0
          ),
          // 4, 샷 구분(프레임)
          [optionsConstant.childOptions['shot'].id]: Object.values(item.childOptions?.shot ?? {}).find(
            (option) => option.isSelected
          ).value,
          // 16, 여체
          [optionsConstant.childOptions.femaleBody.id]: Object.values(item.childOptions?.femaleBody ?? {}).find(
            (option) => option.isSelected
          ).value,
          // 18, 체위
          [optionsConstant.childOptions.sexualPosition.id]: selectedSexualPosition,
          // 22, 신체부위
          [optionsConstant.childOptions.sexualBodyPart.id]: Object.values(item.childOptions?.sexualBodyPart ?? {})
            .filter((option) => option.isSelected)
            .map((option) => String(option.value)),
          // 25, 가로앵글
          [optionsConstant.childOptions['angleFirst'].id]: Object.values(item.childOptions?.angleFirst ?? {})
            .filter((option) => option.isSelected)
            .map((option) => String(option.value)),
          // 26, 세로앵글
          [optionsConstant.childOptions['angleSecond'].id]: Object.values(item.childOptions?.angleSecond ?? {}).find(
            (option) => option.isSelected
          ).value
        },
        comic: { name: inputKeyword.trim() },
        sequence: sequenceId ? { id: sequenceId } : null
      }));

    if (addImages.length > 0) {
      requestBody.addImages = addImages;
    }

    // 변경된 내용이 없어 requestBody가 빈 객체일 경우 return
    if (Object.keys(requestBody).length === 0) {
      setIsRequestPending(false); //저장버튼 활성화
      return;
    }

    patchImageDataMutate(requestBody);
  };

  // image 파일명 생성 함수
  const createImageName = (
    inputKeyword: string,
    imageId: number,
    listLength: number,
    index: number,
    type: string,
    sequenceId?: number
  ) => {
    const ext = type.split('/')[1];

    if (listLength !== 1 && sequenceId) {
      return `${inputKeyword}_${sequenceId}_${index + 1}.${ext}`;
    } else {
      return `${inputKeyword}_${imageId + index}.${ext}`;
    }
  };

  const handleModalConfirmClick = () => {
    if (modalType === ImageUploaderModalType.IMAGE_UPLOAD_CANCEL) {
      navigate(PathnameEnum.IMAGE_UPLOADER_ADULTS_ONLY_IMAGE_LIST_PAGE);
    } else if (modalType === ImageUploaderModalType.IMAGE_DELETE_CONFIRM) {
      if (!focusedImage) return;

      // 이미지 삭제
      const filtered = imageList
        .filter((item) => item !== focusedImage)
        .map((item) => ({
          ...item,
          type: (item as IUploadImageListItem).type || item.name?.split('.').pop() || 'jpg'
        }));

      const newImageList = filtered.map((item, idx) => ({
        ...item,
        order: idx + 1,
        name: createImageName(inputKeyword, lastImageId + 1, filtered.length, idx, item.type, lastSequenceId + 1)
      }));

      setImageList(newImageList);

      const targetIndex = focusedImage.order ? focusedImage.order - 1 : 0;
      const targetImage = newImageList[targetIndex];

      // newImageList의 length가 1 이상이므로 targetImage가 존재할 경우에만 실행
      if (targetImage) {
        // newImageList를 사용하여 handleImageFocus 로직 직접 실행
        setFocusedImageIndex(targetIndex);

        setImageList((prev) => {
          return prev.map((item, idx) => ({
            ...item,
            isFocus: idx === targetIndex
          }));
        });

        // 새로 업로드된 이미지의 경우 POST /images/presigned-url 요청을 invoke
        if (!targetImage?.imageId && !targetImage?.key && !targetImage?.presignedUrl) {
          sendPostPresignedUrl(newImageList[targetIndex], targetIndex);
        }

        // 기존에 업로드된 이미지의 경우 focusedImage의 정보를 불러오기
        if (targetImage?.imageId && (!targetImage?.childOptions || !targetImage?.aiRecommendedChildOptions)) {
          getImageDetail(newImageList[targetIndex], targetIndex);
        }
      }

      setIsModalVisible(false);
    } else if (modalType === ImageUploaderModalType.IMAGE_UPLOAD_SUCCESS) {
      setIsModalVisible(false);
      navigate(PathnameEnum.IMAGE_UPLOADER_ADULTS_ONLY_IMAGE_LIST_PAGE);
    } else if (modalType === ImageUploaderModalType.OMIT_REQUIRED_INPUT) {
      setIsModalVisible(false);
      setIsSaveAttempted(true); // 저장 시도 플래그 ON. 모달 닫은 후 error UI 노출
    }
  };

  const handleSelectedSexualPositionChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedSexualPosition(event.target.value as SexualPositionChildOptionValueEnum);
  };

  // 작품명 인풋 입력시
  const handleInputKeywordChange = (event: React.SyntheticEvent, newValue: string) => {
    // 일반 문자 입력 시: 상태 업데이트
    if (newValue.trim().length > 0) {
      setInputKeyword(newValue);
      handleAutocompleteKeywordChange(newValue);
    } else {
      setInputKeyword('');
      handleAutocompleteKeywordChange(newValue);
    }
  };

  // 작품명 오토컴플리트 리스트 불러오기에 이용할 키워드 setState
  const handleAutocompleteKeywordChange = useCallback(
    debounce((newValue: string) => {
      setAutocompleteKeyword(newValue);
    }, 500),
    []
  );

  // 작품명 오토컴플리트 리스트에서 키워드 선택 시 추가
  const handleAutocompleteChange = (event: React.SyntheticEvent, newValue: string | null) => {
    if (newValue && typeof newValue === 'string' && newValue.trim().length > 0) {
      setAutocompleteKeyword('');
      setInputKeyword(newValue);
      handleComicNameUpdate(newValue);

      // debounce된 함수 호출 취소하여 setAutocompleteKeyword(value) 재실행 방지
      handleAutocompleteKeywordChange.cancel();
    }
  };

  // 작품명 변경 후 SearchInput blur될 시 imageList 각 요소 name을 업데이트
  const handleComicNameUpdate = (value: string) => {
    setImageList((prev) =>
      prev.map((item, idx) => ({
        ...item,
        name: createImageName(value.trim(), lastImageId + 1, prev.length, idx, item.type, lastSequenceId + 1)
      }))
    );
  };

  // OS 확인
  const checkIfMacOS = (): boolean => {
    // userAgent 문자열을 통한 OS 확인
    const userAgent = navigator.userAgent.toLowerCase();

    // macOS의 다양한 식별자 확인
    const macIdentifiers = ['macintosh', 'mac os x', 'mac_powerpc'];
    return macIdentifiers.some((identifier) => userAgent.includes(identifier));
  };

  // 파일 유효성 검사 함수
  const validateFiles = (files: File[]) => {
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg'];

    const isMacOS = checkIfMacOS();
    const maxSize = isMacOS
      ? 10 * 1000 * 1000 // MacOS: 10,000,000 bytes
      : 10 * 1024 * 1024; // Windows: 10,485,760 bytes

    const invalidTypeFiles = files.filter((file) => !validTypes.includes(file.type));
    const oversizedFiles = files.filter((file) => file.size > maxSize);

    // 잘못된 형식의 파일이 있는 경우
    if (invalidTypeFiles.length > 0) {
      setModalType(ImageUploaderModalType.IMAGE_FILE_TYPE_ERROR);
      setIsModalVisible(true);
      setModalTitle(`${invalidTypeFiles.length}장의 파일 업로드가 실패했습니다.`);
      setModalContent('jpg, png 형식만 등록이 가능합니다.');
      return files.filter((file) => validTypes.includes(file.type) && file.size <= maxSize);
    }

    // 용량 초과 파일이 있는 경우
    if (oversizedFiles.length > 0) {
      setModalType(ImageUploaderModalType.IMAGE_FILE_SIZE_ERROR);
      setIsModalVisible(true);
      setModalTitle(`${oversizedFiles.length}장의 파일 업로드가 실패했습니다.`);
      setModalContent('10MB 이하의 이미지만 등록이 가능합니다.');
      return files.filter((file) => file.size <= maxSize && validTypes.includes(file.type));
    }

    return files;
  };

  // '파일 직접 선택' 버튼 클릭시 이미지 첨부
  const handleImageUploadButtonClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);
    const validFiles = validateFiles(files);
    const sortedFiles = validFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })); //여러 장 업로드시 원본파일명 이름순 자연 정렬

    // 새로 추가될 이미지 객체 생성
    const newImageItems: IUploadImageListItem[] = sortedFiles.map((file) => ({
      name: '', // 아래에서 덮어씀
      url: URL.createObjectURL(file),
      tags: [],
      order: 0, // 아래에서 덮어씀
      comic: { name: inputKeyword },
      childOptions: { sexualPosition: selectedSexualPosition },
      isFocus: false,
      type: file.type,
      file: file
    }));

    // 기존 리스트에도 type 필드 보장
    const oldImageItems = imageList.map((item) => ({
      ...item,
      type: (item as IUploadImageListItem).type || item.name?.split('.').pop() || 'jpg'
    }));

    const mergedList = [...oldImageItems, ...newImageItems];

    setImageList(
      mergedList.map((item, idx) => ({
        ...item,
        name: createImageName(inputKeyword, lastImageId + 1, mergedList.length, idx, item.type, lastSequenceId + 1),
        order: idx + 1
      }))
    );
  };

  // 파일 drag and drop하여 이미지 첨부
  const handleAttachDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const files = Array.from(e.dataTransfer.files);
    const validFiles = validateFiles(files);
    const sortedFiles = validFiles.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })); //여러 장 업로드시 원본파일명 이름순 자연 정렬

    const newImageItems: IUploadImageListItem[] = sortedFiles.map((file) => ({
      name: '', // 아래에서 덮어씀
      url: URL.createObjectURL(file),
      tags: [],
      order: 0, // 아래에서 덮어씀
      comic: { name: inputKeyword },
      childOptions: { sexualPosition: selectedSexualPosition },
      isFocus: false,
      type: file.type,
      file: file
    }));

    const oldImageItems = imageList.map((item) => ({
      ...item,
      type: (item as IUploadImageListItem).type || item.name?.split('.').pop() || 'jpg'
    }));

    const mergedList = [...oldImageItems, ...newImageItems];

    setImageList(
      mergedList.map((item, idx) => ({
        ...item,
        name: createImageName(inputKeyword, lastImageId + 1, mergedList.length, idx, item.type, lastSequenceId + 1),
        order: idx + 1
      }))
    );
  };

  const handleAttachDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleAttachDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // 이미지 순서 변경: 이미지 드래그 시작시 드래그하는 아이템의 인덱스 저장
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  // 이미지 순서 변경: 드래그 중인 아이템을 다른 아이템 위에 올려놓았을 때
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // 이미지 순서 변경: 드래그 중인 아이템을 드롭했을 때
  const handleDragDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      const dragIndex = Number(e.dataTransfer.getData('text/plain'));

      if (dragIndex === dropIndex) return;

      setImageList((prev) => {
        // type 필드 보장
        const newList = prev.map((item) => ({
          ...item,
          type: (item as IUploadImageListItem).type || item.name?.split('.').pop() || 'jpg'
        }));
        const [draggedItem] = newList.splice(dragIndex, 1);
        newList.splice(dropIndex, 0, draggedItem);

        return newList.map((item, idx) => ({
          ...item,
          order: idx + 1,
          name: createImageName(inputKeyword, lastImageId + 1, newList.length, idx, item.type, lastSequenceId + 1)
        }));
      });
    },
    [inputKeyword, lastImageId, lastSequenceId]
  );

  // 특정 이미지 삭제
  const handleImageDelete = () => {
    setModalType(ImageUploaderModalType.IMAGE_DELETE_CONFIRM);
    setModalTitle('이미지를 삭제하시겠습니까?');
    setModalContent('이 작업은 되돌릴 수 없습니다.');
    setIsModalVisible(true);
  };

  // childOptions 업데이트 함수
  const updateFocusedImageChildOptions = (key: string, value: any) => {
    if (focusedImageIndex === null) return;
    setImageList((prev) => {
      // 기존 리스트 복사
      const newList = prev.map((item, idx) => {
        if (idx !== focusedImageIndex) return item;
        // childOptions도 새 객체로 복사
        return {
          ...item,
          childOptions: {
            ...item.childOptions,
            [key]: value
          }
        };
      });
      return newList;
    });
  };

  // tags 배열에 태그를 추가하는 함수
  const addFocusedImageTag = (value: string) => {
    if (focusedImageIndex === null) return;
    setImageList((prev) => {
      // 기존 리스트 복사
      const newList = prev.map((item, idx) => {
        if (idx !== focusedImageIndex) return item;
        // 기존 tags 배열에 value가 이미 존재하는지 확인
        if (item.tags && item.tags.includes(value)) return item;
        return {
          ...item,
          tags: item.tags && item.tags.length > 0 ? [...item.tags, value] : [value]
        };
      });
      return newList;
    });
  };

  // 스페이스바 입력 시 태그 추가, handleTagInputKeywordChange보다 먼저 실행됨
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case ' ': {
        e.preventDefault(); // 스페이스 입력 방지
        if (tagInputKeyword.trim().length > 0) {
          addFocusedImageTag(tagInputKeyword.trim());
          setIsTagging(true); // 태그 추가 플래그 설정
          setTagAutocompleteKeyword('');
          setTagInputKeyword('');
        }
        break;
      }
      default: {
        return;
      }
    }
  };

  // 태그 인풋 입력시
  const handleTagInputKeywordChange = (event: React.SyntheticEvent, newValue: string) => {
    if (isTagging) {
      setIsTagging(false); // 태그 추가 후 `onInputChange` 무시
      return;
    }

    setTagInputKeyword(newValue);
    handleTagAutocompleteKeywordChange(newValue.trim());
  };

  // 작품명 오토컴플리트 리스트 불러오기에 이용할 키워드 setState
  const handleTagAutocompleteKeywordChange = useCallback(
    debounce((newValue: string) => {
      setTagAutocompleteKeyword(newValue);
    }, 500),
    []
  );

  // 작품명 오토컴플리트 리스트에서 키워드 선택 시 추가
  const handleTagAutocompleteChange = (event: React.SyntheticEvent, newValue: string | null) => {
    if (newValue && typeof newValue === 'string' && newValue.trim().length > 0) {
      addFocusedImageTag(newValue.trim());
      setTagAutocompleteKeyword('');
      setTagInputKeyword('');

      // debounce된 함수 호출 취소하여 setTagAutocompleteKeyword(value) 재실행 방지
      handleTagAutocompleteKeywordChange.cancel();
    }
  };

  // tags 배열에 태그를 제거하는 함수
  const deleteFocusedImageTag = (value: string) => {
    if (focusedImageIndex === null) return;
    setImageList((prev) => {
      const newList = prev.map((item, idx) => {
        if (idx !== focusedImageIndex) return item;
        return {
          ...item,
          tags: item.tags && item.tags.length > 0 ? item.tags.filter((tag) => tag !== value) : null
        };
      });
      return newList;
    });
  };

  // 이미지 갯수에 따른 저장 버튼 분기
  useEffect(() => {
    if (imageList.length > 0) {
      setNeedImageAttach(false);
    } else {
      setNeedImageAttach(true);
    }
  }, [imageList]);

  // 작품명 입력 여부에 따른 이미지 업로드 문구&파일 직접 선택 버튼 분기
  useEffect(() => {
    if (inputKeyword && inputKeyword.trim().length > 0) {
      setIsUploadDisabled(false);
    } else {
      setIsUploadDisabled(true);
    }
  }, [inputKeyword]);

  // 이미지 업로드 페이지 첫 이미지 업로드시 0번 자동 focus
  useEffect(() => {
    if (isCreate && prevImageListLength === 0 && imageList.length > 0) {
      handleImageFocus(imageList[0], 0);
    }
    setPrevImageListLength(imageList.length);
  }, [imageList.length, isCreate]);

  //GET /admin/images/${imageId} response.data를 setImageList
  useEffect(() => {
    if (!isCreate && fetchedImageData) {
      // 작품명 setState
      if (fetchedImageData.comic && fetchedImageData.comic.name) {
        setOriginalComic(fetchedImageData.comic);
        setInputKeyword(fetchedImageData.comic.name);
      }

      const newImageList = fetchedImageData.images.map((image, index) => {
        const ext = image.s3Url?.split('.').pop() ?? 'jpg';

        return {
          name: createImageName(
            fetchedImageData.comic?.name ?? '',
            image.id,
            fetchedImageData.images.length,
            index,
            `image/${ext}`,
            image.sequence?.id
          ),
          url: image.s3Url,
          comic: {
            id: fetchedImageData.comic?.id,
            name: fetchedImageData.comic?.name ?? ''
          },
          order: fetchedImageData.images.length === 1 ? undefined : index + 1, // 단일컷인 경우 order가 undefined
          childOptions: null,
          tags: null,
          isFocus: false,
          type: `image/${ext}`,
          imageId: image.id,
          uniqueFileName: image.uniqueFileName
        };
      });

      setOriginalImageList(newImageList);
      setImageList(newImageList);

      // 이미지 수정 페이지에서 GET /admin/images/${imageId} fetch 직후 focusedImage의 정보를 불러오기 위함
      const targetIndex = fetchedImageData.images.findIndex((image) => image.id === Number(imageId));
      if (targetIndex !== -1) {
        // newImageList를 사용하여 handleImageFocus 로직 직접 실행
        setFocusedImageIndex(targetIndex);

        setImageList((prev) => {
          return prev.map((item, idx) => ({
            ...item,
            isFocus: idx === targetIndex
          }));
        });

        const targetImage = newImageList[targetIndex];
        if (targetImage?.imageId && !targetImage.childOptions) {
          getImageDetail(targetImage, targetIndex);
        }
      }
    }
  }, [fetchedImageData]);

  return {
    // 브레드크럼 클릭시
    handleBreadCrumbClick,

    // 페이지명 분기
    isCreate,

    // 페이지 취소 버튼
    handlePageCancelClick,

    // 페이지 저장 버튼
    needImageAttach,
    isRequestPending,
    handleSaveButtonClick,

    // 이미지 업로드 문구&파일 직접 선택 버튼 분기 관련
    isUploadDisabled,

    // 이미지 업로드 페이지: 상단 체위 필터 및 작품명 인풋
    selectedSexualPosition,
    handleSelectedSexualPositionChange,
    inputKeyword,
    autocompleteList,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleComicNameUpdate,

    // 이미지 업로드 페이지: 하단 이미지 업로드 관련
    imageList,
    handleImageUploadButtonClick,
    handleAttachDrop,
    handleAttachDragOver,
    handleAttachDragEnter,
    handleDragStart,
    handleDragOver,
    handleDragDrop,

    // 우측 이미지 정보란 관련
    focusedImage,
    handleImageFocus,
    handleImageDelete,
    updateFocusedImageChildOptions,
    isFetchAIRecommendationLoading,
    tagInputKeyword,
    tagAutocompleteList,
    handleTagKeyDown,
    handleTagInputKeywordChange,
    handleTagAutocompleteChange,
    addFocusedImageTag,
    deleteFocusedImageTag,

    //에러 관련
    modalType,
    isModalVisible,
    setIsModalVisible,
    modalTitle,
    modalContent,
    handleModalConfirmClick,
    isSaveAttempted,

    // 이미지 수정 페이지 관련
    fetchedImageData
  };
};
